import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatChipGrid, MatChipRow, MatChipRemove, MatChipInput, MatChipInputEvent } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { catchError, debounceTime, distinctUntilChanged, finalize, firstValueFrom, map, of, switchMap, tap } from 'rxjs';
import { ManageLocationBody, ManageLocationDialogData, Location, LocationTypeChoice } from 'src/app/models/location.model';
import { LocationRelatableTypeEnum, getLocationRelatableTypeLabel } from 'src/app/enums/location-relatable-type.enum';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService, UserService, EmployeeService } from 'src/app/services';
import { LocationIp } from 'src/app/models/location-ip.model';
import { GeoCoordinates } from 'src/app/interfaces/geo-coordinates.interface';
import { LocationMapPickerComponent, LocationMapSelection } from '../location-map-picker/location-map-picker.component';
import { PotentialClient } from 'src/app/models/potential-client.interface';
import { CompanyChoice } from 'src/app/models/company.model';
import { getLocationDialogModeLabel, LocationDialogModeEnum } from 'src/app/enums/location-dialog-mode.enum';
import { CompanySearchDialogComponent } from '../../company-search-dialog/company-search-dialog.component';

type LocationTypeOption = LocationRelatableTypeEnum | null;

@Component({
  selector: 'app-manage-location-dialog',
  templateUrl: './manage-location-dialog.component.html',
  styleUrls: ['./manage-location-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatChipGrid,
    MatChipRow,
    MatChipRemove,
    MatChipInput,
    MatIcon,
    MatDialogActions,
    MatButton,
    MatProgressSpinner,
    MatSelect,
    MatOption,
    MatAutocomplete,
    MatAutocompleteTrigger,
    LocationMapPickerComponent,
    CompanySearchDialogComponent,
  ],
})
export class ManageLocationDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('ipInput') ip_input?: ElementRef<HTMLInputElement>;

  ips: string[] = [];
  type_options: LocationTypeChoice[] = [];
  filtered_potential_clients: PotentialClient[] = [];

  readonly saving = signal(false);
  readonly selected_coordinates = signal<GeoCoordinates | null>(null);
  readonly coordinates_invalid = signal(false);
  readonly map_load_error = signal<string | null>(null);
  readonly manual_coordinates_error = signal<string | null>(null);

  readonly potential_client_search = new UntypedFormControl('');
  readonly potential_client_search_term = signal('');
  readonly potential_client_search_loading = signal(false);

  private readonly data = inject<ManageLocationDialogData>(MAT_DIALOG_DATA);
  private readonly dialog_ref = inject(MatDialogRef<ManageLocationDialogComponent>);
  private readonly api = inject(ApiService);
  private readonly permissions = inject(PermissionsService);
  private readonly user = inject(UserService);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly ip_pattern = /^(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}|([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4})$/;
  private readonly employee = inject(EmployeeService);

  readonly is_choose_relatable = 'choose_relatable' in this.data && this.data.choose_relatable;
  readonly mode: LocationDialogModeEnum = 'choose_relatable' in this.data ? LocationDialogModeEnum.Create : this.data.mode;
  readonly mode_label = getLocationDialogModeLabel(this.mode);
  readonly LocationDialogModeEnum = LocationDialogModeEnum;
  readonly LocationRelatableTypeEnum = LocationRelatableTypeEnum; // needed for template comparisons
  readonly location_type_label =
    'choose_relatable' in this.data
      ? ''
      : getLocationRelatableTypeLabel('location' in this.data ? this.data.location.relatable_type : this.data.relatable_type);
  readonly is_meeting_mode = signal<boolean>(false);
  readonly meeting_company_name = signal('');

  readonly form = this.fb.group({
    location_type: [null as LocationTypeOption],
    company_id: [null as number | null],
    potential_client_id: [null as number | null],
    name: ['', [Validators.required, Validators.maxLength(255)]],
    lat: [null, [Validators.required]],
    lon: [null, [Validators.required]],
    ips: [[]],
  });

  ngOnInit(): void {
    if (this.is_choose_relatable) {
      this.initChooseRelatableContext();
    }

    if (this.mode === LocationDialogModeEnum.Create || !('location' in this.data)) {
      return;
    }

    const { location } = this.data;

    this.form.patchValue({
      name: location.name,
      lat: location.lat,
      lon: location.lon,
    });
    this.selected_coordinates.set({ lat: location.lat, lon: location.lon });

    this.ips = (location.location_ips ?? []).map((location_ip: LocationIp) => location_ip.ip);
    this.syncIpsControl();
  }

  ngAfterViewInit(): void {
    if (this.mode === LocationDialogModeEnum.View) {
      this.form.disable();
    }
    this.lockCompanyIfMeetingMode();
  }

  setLocation(location: LocationMapSelection): void {
    const coordinates: GeoCoordinates = {
      lat: location.lat,
      lon: location.lon,
    };

    this.manual_coordinates_error.set(null);
    this.selected_coordinates.set(coordinates);
    this.form.patchValue({
      lat: location.lat,
      lon: location.lon,
    });
    this.form.controls.lat.markAsTouched();
    this.form.controls.lon.markAsTouched();
    this.form.controls.lat.updateValueAndValidity();
    this.form.controls.lon.updateValueAndValidity();

    const name = location.name?.trim();

    if (name == null || name === '') {
      return;
    }

    this.form.controls.name.setValue(name);
    this.form.controls.name.markAsTouched();
    this.form.controls.name.updateValueAndValidity();
  }

  setMapLoadError(load_error: string | null): void {
    this.map_load_error.set(load_error);

    if (load_error == null) {
      this.manual_coordinates_error.set(null);
      return;
    }
  }

  addIp(event: MatChipInputEvent): void {
    this.pushIp(event.value);
    event.chipInput?.clear();
  }

  onCancel(): void {
    this.dialog_ref.close();
  }

  submit(): void {
    if (!this.validateBeforeSubmit()) {
      return;
    }

    this.saving.set(true);

    const body: ManageLocationBody = {
      name: this.form.value.name,
      lat: this.form.value.lat,
      lon: this.form.value.lon,
      ips: this.ips,
      relatable_type: this.resolveRelatableType(),
      relatable_id: this.resolveRelatableId(),
    };

    const request$ =
      this.mode === LocationDialogModeEnum.Edit && 'location' in this.data
        ? this.api.updateLocation(this.data.location.id, body)
        : this.api.createLocation(body);

    request$.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe({
      next: (location: Location): void => this.dialog_ref.close(location),
      error: (): void => this.saving.set(false),
    });
  }

  removeIp(ip: string): void {
    const index = this.ips.indexOf(ip);
    if (index >= 0) {
      this.ips.splice(index, 1);
      this.syncIpsControl();
    }
  }

  displayPotentialClient(client: PotentialClient | string | null): string {
    if (client == null || typeof client === 'string') {
      return client ?? '';
    }

    if (client.company_name != null && client.company_name !== '') {
      return `${client.full_name} (${client.company_name})`;
    }

    return client.full_name;
  }

  onPotentialClientSelected(event: MatAutocompleteSelectedEvent): void {
    const client = event.option.value as PotentialClient;
    this.form.patchValue({ potential_client_id: client.id });
    this.potential_client_search.setValue(this.displayPotentialClient(client), { emitEvent: false });
    this.potential_client_search_term.set('');
    this.filtered_potential_clients = [];
  }

  onCompanySelected(company: CompanyChoice): void {
    this.form.patchValue({ company_id: company.id });
  }

  private initChooseRelatableContext(): void {
    this.buildTypeOptions();

    const default_type = this.type_options[0]?.value ?? LocationRelatableTypeEnum.User;
    this.form.patchValue({ location_type: default_type });
    this.applyRelatableValidators(default_type);
    this.initPotentialClientSearch();

    this.form.controls.location_type.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(type => {
      this.applyRelatableValidators(type as LocationTypeOption);
    });
  }

  private buildTypeOptions(): void {
    const options: LocationTypeChoice[] = [
      { value: LocationRelatableTypeEnum.User, label: getLocationRelatableTypeLabel(LocationRelatableTypeEnum.User) },
    ];

    if (this.permissions.isWorker()) {
      options.push({ value: null, label: getLocationRelatableTypeLabel(null) });
    }

    if (this.permissions.isAdmin() || this.permissions.isDepartment('rrhh') || this.permissions.isProductTrioInAnyCompanyProduct()) {
      options.push({ value: LocationRelatableTypeEnum.Company, label: getLocationRelatableTypeLabel(LocationRelatableTypeEnum.Company) });
    }

    if (this.permissions.isAdmin() || this.permissions.isDepartment('comercial') || this.permissions.isAccountManager()) {
      options.push({
        value: LocationRelatableTypeEnum.PotentialClient,
        label: getLocationRelatableTypeLabel(LocationRelatableTypeEnum.PotentialClient),
      });
    }

    this.type_options = options;
  }

  private initPotentialClientSearch(): void {
    this.potential_client_search.valueChanges
      .pipe(
        tap(search => {
          const term = typeof search === 'string' ? search.trim() : '';
          this.potential_client_search_term.set(term);
          if (term.length < 3) {
            this.filtered_potential_clients = [];
            this.potential_client_search_loading.set(false);
          }
        }),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(search => {
          const term = typeof search === 'string' ? search.trim() : '';
          if (term.length < 3) {
            return of([] as PotentialClient[]);
          }

          this.potential_client_search_loading.set(true);
          return this.api.getPotentialClientsPaged({ search: term, per_page: 20 }).pipe(
            map(response => response.data ?? []),
            catchError(() => of([] as PotentialClient[])),
            finalize(() => this.potential_client_search_loading.set(false))
          );
        }),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(clients => {
        this.filtered_potential_clients = clients;
      });
  }

  private applyRelatableValidators(type: LocationTypeOption): void {
    const company_control = this.form.controls.company_id;
    const potential_client_control = this.form.controls.potential_client_id;

    company_control.clearValidators();
    potential_client_control.clearValidators();

    if (type === LocationRelatableTypeEnum.Company) {
      company_control.setValidators([Validators.required]);
    } else {
      company_control.setValue(null);
    }

    if (type === LocationRelatableTypeEnum.PotentialClient) {
      potential_client_control.setValidators([Validators.required]);
    } else {
      potential_client_control.setValue(null);
      this.potential_client_search.setValue('', { emitEvent: false });
      this.potential_client_search_term.set('');
      this.filtered_potential_clients = [];
      this.potential_client_search_loading.set(false);
    }

    company_control.updateValueAndValidity();
    potential_client_control.updateValueAndValidity();
  }

  private validateBeforeSubmit(): boolean {
    this.syncIpsControl();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    return this.form.valid;
  }

  private pushIp(raw: string): boolean {
    const value = (raw || '').trim();
    if (value !== '' && this.ip_pattern.test(value) && !this.ips.includes(value)) {
      this.ips.push(value);
      this.syncIpsControl();
      return true;
    }
    return false;
  }

  private syncIpsControl(): void {
    const ips_control = this.form.get('ips')!;
    ips_control.setValue([...this.ips]);
    ips_control.markAsDirty();
    ips_control.markAsTouched();
  }

  private resolveRelatableType(): LocationRelatableTypeEnum | null {
    if (this.is_choose_relatable) {
      return this.form.value.location_type;
    }

    if (this.mode === LocationDialogModeEnum.Create && 'relatable_type' in this.data) {
      return this.data.relatable_type;
    }

    return 'location' in this.data ? this.data.location.relatable_type : null;
  }

  private resolveRelatableId(): number | null {
    if (this.is_choose_relatable) {
      switch (this.form.value.location_type) {
        case LocationRelatableTypeEnum.User:
          return this.user.getCurrentUser().id;
        case LocationRelatableTypeEnum.Company:
          return this.form.value.company_id;
        case LocationRelatableTypeEnum.PotentialClient:
          return this.form.value.potential_client_id;
        default:
          return null;
      }
    }

    if (this.mode === LocationDialogModeEnum.Create && 'relatable_id' in this.data) {
      return this.data.relatable_id;
    }

    return 'location' in this.data ? this.data.location.relatable_id : null;
  }

  private async lockCompanyIfMeetingMode(): Promise<void> {
    if (this.mode !== LocationDialogModeEnum.Create || !this.employee.isMeetingOngoing()) {
      return;
    }

    this.is_meeting_mode.set(true);
    const meeting_mode_company_product_id = await firstValueFrom(this.employee.meetingModeCompanyProduct);
    const meeting_mode_company_product = await firstValueFrom(this.api.getCompanyProduct(meeting_mode_company_product_id.toString()));
    const company = await firstValueFrom(this.api.getCompany(meeting_mode_company_product.company_id.toString()));
    this.meeting_company_name.set(company.fact_name ?? '');
    this.form.patchValue({
      location_type: LocationRelatableTypeEnum.Company,
      company_id: company.id,
    });
  }
}
